import { useState, useCallback, useMemo, useEffect } from "react";
import { useGetProjectMembershipQuery } from "../app/accounts/memberships";
import { useUpdateShotStatusMutation, useUpdateShotStatusAnimatedMutation } from "../app/services/shots";
// @ts-ignore
import { SHOT_STATUS } from "../constant/shotStatus";
import toast from "react-hot-toast";

interface UseShotApprovalParams {
  /** プロジェクトID */
  projectId: string;
  /** ショットID */
  shotId: string;
  /** ショットの現在のステータス */
  currentStatus: number;
  /** ステータス変更成功時に呼ばれるコールバック */
  onStatusChanged?: () => void;
  /** true の場合、フック内部では onStatusChanged を呼ばない（外部でアニメーション制御する場合） */
  skipOnStatusChanged?: boolean;
  /** true の場合、invalidatesTags なしの mutation を使う（アニメーション中の自動 refetch を防ぐ） */
  useAnimatedMutation?: boolean;
}

interface UseShotApprovalReturn {
  /** 現在APPROVEDかどうか */
  isApproved: boolean;
  /** 検収ボタンをクリック可能か */
  canApprove: boolean;
  /** API呼び出し中か */
  isLoading: boolean;
  /** 検収ON/OFFを切り替える（Promise を返す） */
  toggleApproval: () => Promise<void>;
  /** ユーザーのポジション */
  userPosition: string | undefined;
  /** 閲覧のみメンバーか（membership 未取得時は true 扱い） */
  isReadOnly: boolean;
}

/**
 * ショット検収ON/OFFの共通ロジックhook
 *
 * - CLIENT / REVIEWER で is_read_only=false の場合のみ検収可能
 * - 検収ON:  status を APPROVED(5) に更新
 * - 検収OFF: CLIENT/REVIEWER は CLIENT_PENDING(4)、PRODUCER/DIRECTOR は PRODUCER_PENDING(3) に戻す
 */
export const useShotApproval = ({
  projectId,
  shotId,
  currentStatus,
  onStatusChanged,
  skipOnStatusChanged = false,
  useAnimatedMutation = false,
}: UseShotApprovalParams): UseShotApprovalReturn => {
  const { data: membershipData } = useGetProjectMembershipQuery(projectId, {
    skip: !projectId,
  });

  const [updateShotStatus, { isLoading: isLoadingNormal }] = useUpdateShotStatusMutation();
  const [updateShotStatusAnimated, { isLoading: isLoadingAnimated }] = useUpdateShotStatusAnimatedMutation();
  const mutate = useAnimatedMutation ? updateShotStatusAnimated : updateShotStatus;
  const isLoading = isLoadingNormal || isLoadingAnimated;

  // 楽観的UI用のローカルステート
  const [optimisticStatus, setOptimisticStatus] = useState<number>(currentStatus);

  // currentStatus が外部から変わった場合に同期
  useEffect(() => {
    setOptimisticStatus(currentStatus);
  }, [currentStatus]);

  // メンバーシップ情報
  const userPosition = membershipData?.data?.position;
  const isReadOnly = membershipData?.data?.is_read_only ?? true;

  // 検収可能かどうか（CLIENT or REVIEWER かつ is_read_only=false）
  const canApprove = useMemo(() => {
    return (
      (userPosition === "CLIENT" || userPosition === "REVIEWER") && !isReadOnly
    );
  }, [userPosition, isReadOnly]);

  // 現在APPROVED系ステータスかどうか（5,6）
  const isApproved =
    optimisticStatus === SHOT_STATUS.APPROVED ||
    optimisticStatus === SHOT_STATUS.APPROVED_WITH_DELIVERY_FILE;

  // 検収取り消し時の戻り先ステータス
  const revertStatus = useMemo(() => {
    if (userPosition === "CLIENT" || userPosition === "REVIEWER") {
      return SHOT_STATUS.CLIENT_PENDING; // 4
    }
    // PRODUCER / DIRECTOR
    return SHOT_STATUS.PRODUCER_PENDING; // 3
  }, [userPosition]);

  // 検収ON/OFFトグル
  const toggleApproval = useCallback(async () => {
    if (!projectId || !shotId || isLoading) return;

    const newStatus = isApproved ? revertStatus : SHOT_STATUS.APPROVED;
    const previousStatus = optimisticStatus;

    // 楽観的に先行更新
    setOptimisticStatus(newStatus);

    try {
      await mutate({
        project_id: projectId,
        shot_id: shotId,
        status: newStatus,
      }).unwrap();

      if (!skipOnStatusChanged) {
        onStatusChanged?.();
      }
    } catch (error) {
      setOptimisticStatus(previousStatus);
      if (!skipOnStatusChanged) {
        toast.error("ステータスの更新に失敗しました");
      }
      console.error("Failed to update shot status:", error);
      throw error;
    }
  }, [
    projectId,
    shotId,
    isLoading,
    isApproved,
    revertStatus,
    optimisticStatus,
    mutate,
    onStatusChanged,
    skipOnStatusChanged,
  ]);

  return {
    isApproved,
    canApprove,
    isLoading,
    toggleApproval,
    userPosition,
    isReadOnly,
  };
};

export default useShotApproval;
